import { isRedoEmployeeEmail } from "@redotech/account-common/redo-employee";
import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { Flex } from "@redotech/arbiter/flex";
import { Text } from "@redotech/arbiter/text";
import { useHandler } from "@redotech/react-util/hook";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { registerRateRefreshRequest } from "@redotech/redo-merchant-app-common/open-order-notifications";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { RedoOutboundLabelsRpcClient } from "@redotech/redo-merchant-fulfillment-rpc-client/client";
import { showNotification } from "@redotech/redo-web/alert";
import {
  memo,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";
import { ProgressNotification } from "./progress-notification";

const POLL_INTERVAL_MS = 5000;

export const RateRefreshNotificationProvider = memo(
  function RateRefreshNotificationProvider({
    children,
  }: {
    children: ReactNode;
  }) {
    const client = useContext(RedoOutboundLabelsRpcClientContext);
    const user = useContext(UserContext);
    const showing = useRef(false);
    const redoEmployee = isRedoEmployeeEmail(user?.email);

    const showProgress = useHandler((teamId: string) => {
      if (!client || showing.current) {
        return;
      }
      showing.current = true;
      showNotification({
        dataTestId: "rate-refresh-status",
        hideCloseX: true,
        noPadding: true,
        notificationKey: "rates",
        onDismiss: () => {
          showing.current = false;
        },
        children: (dismiss) => (
          <RateRefreshProgressDisplay
            client={client}
            dismiss={dismiss}
            teamId={teamId}
          />
        ),
      });
    });

    const showOptIn = useHandler((teamId: string) => {
      if (!client || showing.current) {
        return;
      }
      showing.current = true;
      showNotification({
        dataTestId: "rate-refresh-status",
        hideCloseX: true,
        noPadding: true,
        notificationKey: "rates",
        onDismiss: () => {
          showing.current = false;
        },
        children: (dismiss) => (
          <RateRefreshOptIn
            client={client}
            dismiss={dismiss}
            onStarted={() => showProgress(teamId)}
          />
        ),
      });
    });

    useEffect(() => {
      if (!client) {
        return;
      }
      const requestRefresh = (teamId: string) => {
        if (redoEmployee) {
          showOptIn(teamId);
          return;
        }
        void client
          .refreshTeamRates({})
          .then((result) => {
            if (result.started) {
              showProgress(teamId);
            }
          })
          .catch((error) => {
            console.error("Failed to start rate refresh:", error);
          });
      };
      const unregisterRequest = registerRateRefreshRequest(requestRefresh);
      return () => {
        unregisterRequest();
      };
    }, [client, redoEmployee, showOptIn, showProgress]);

    return children;
  },
);

const RateRefreshOptIn = memo(function RateRefreshOptIn({
  client,
  dismiss,
  onStarted,
}: {
  client: RedoOutboundLabelsRpcClient;
  dismiss: () => void;
  onStarted: () => void;
}) {
  const [pending, setPending] = useState(false);

  const refresh = useHandler(async () => {
    setPending(true);
    try {
      const result = await client.refreshTeamRates({});
      dismiss();
      if (result.started) {
        onStarted();
      }
    } catch (error) {
      console.error("Failed to refresh rates:", error);
      setPending(false);
    }
  });

  return (
    <Flex dir="column" gap="none" w="xxs">
      <Flex align="center" justify="space-between" px="xl" py="md">
        <Text fontSize="sm" fontWeight="semibold">
          Refresh rates?
        </Text>
        <RedoButton IconTrailing={XIcon} onClick={dismiss} size="xs" />
      </Flex>
      <Flex dir="column" gap="sm" pb="md" px="xl" w="full">
        <Text fontSize="xs" textColor="secondary">
          Shipping settings changed. Rates stay as they are until you refresh
          them.
        </Text>
        <RedoButton
          disabled={pending}
          hierarchy="primary"
          onClick={() => {
            void refresh();
          }}
          pending={pending}
          size="xs"
          text="Refresh rates"
        />
      </Flex>
    </Flex>
  );
});

const RateRefreshProgressDisplay = memo(function RateRefreshProgressDisplay({
  client,
  dismiss,
  teamId,
}: {
  client: RedoOutboundLabelsRpcClient;
  dismiss: () => void;
  teamId: string;
}) {
  const [started, setStarted] = useState(0);
  const [total, setTotal] = useState(0);
  const [done, setDone] = useState(false);
  const [ellipsis, setEllipsis] = useState(".");

  useEffect(() => {
    if (done) {
      return;
    }
    const interval = setInterval(() => {
      setEllipsis((current) => (current.length >= 3 ? "." : current + "."));
    }, 1000);
    return () => clearInterval(interval);
  }, [done]);

  useEffect(() => {
    let cancelled = false;
    let sawRefresh = false;

    const timer = setInterval(() => {
      void poll();
    }, POLL_INTERVAL_MS);

    async function poll() {
      try {
        const result = await client.getRefreshTeamRatesProgress({});
        if (cancelled) {
          return;
        }
        if (result.refresh) {
          sawRefresh = true;
          setStarted(result.refresh.started);
          setTotal(result.refresh.total);
          return;
        }
        if (sawRefresh) {
          setDone(true);
          clearInterval(timer);
        }
      } catch (error) {
        console.error("Failed to poll rate refresh progress:", error);
      }
    }

    void poll();

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [client]);

  const percentage = done
    ? 100
    : total > 0
      ? Math.round((started / total) * 100)
      : 0;

  return (
    <ProgressNotification
      dismiss={dismiss}
      percentage={percentage}
      teamId={teamId}
      title={done ? "Rates refreshed" : `Refreshing rates${ellipsis}`}
    />
  );
});
